import {DATE_VALIDATION_MESSAGE, DATE_VALIDATION_TYPE, SCHEDULE_DROPDOWN} from '@/utils/constants/commonConstants'

export const useDatePickerInput = (fieldName, validationRules = []) => {
  // this function is for type start date
  const validateStartDateWithCurrentDate = startDate => {
    const today = new Date()
    today.setHours(0, 0, 0, 0) // Normalize to start of the day
    if (startDate) {
      const [month, day, year] = startDate.split('/').map(Number) // Ensure correct parsing
      const parsedStartDate = new Date(year, month - 1, day) // Month is 0-based in JS
      if (parsedStartDate < today) {
        return true
      }
    }
    return false
  }

  const validateStartDateIsToday = startDate => {
    const today = new Date()
    today.setHours(0, 0, 0, 0) // Normalize to start of the day
    if (startDate) {
      const [month, day, year] = startDate.split('/').map(Number) // Ensure correct parsing
      const parsedStartDate = new Date(year, month - 1, day) // Month is 0-based in JS
      if (parsedStartDate.getTime() === today.getTime()) {
        return true
      }
    }
    return false
  }

  // this function is for validating start date with end date

  const validateStartDateAndEndDate = (selectedEndDate, selectedStartDate) => {
    const endDate = new Date(selectedEndDate) // Convert both dates to Date objects (ensure they are at midnight)
    const startDate = new Date(selectedStartDate)
    endDate.setHours(0, 0, 0, 0) // Normalize times to midnight (prevents issues with time differences)
    startDate.setHours(0, 0, 0, 0)
    const diffInMs = endDate - startDate // Calculate the difference in milliseconds
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24) // Convert milliseconds to days (1 day = 1000 * 60 * 60 * 24 ms)

    return diffInDays
  }
  const startDateShouldBeGreater = (selectedEndDate, selectedStartDate) => {
    const endDate = new Date(selectedEndDate).getTime()
    const startDate = new Date(selectedStartDate).getTime()

    if (startDate >= endDate) {
      return false
    }
    return true
  }

  // this function is for validating if the selected date is in the same year as the current date
  const validateSameYear = selectedDate => {
    if (!selectedDate) return false
    const today = new Date()
    const [month, day, year] = selectedDate.split('/').map(Number) // Ensure correct parsing
    if (!month || !day || !year) return false
    const parsedSelectedDate = new Date(year, month - 1, day) // Month is 0-based in JS
    if (!parsedSelectedDate) return false

    return parsedSelectedDate?.getFullYear() === today?.getFullYear()
  }

  // this function is for validating if the selected date is in the same month as the current date
  const validateSameMonth = selectedDate => {
    if (!selectedDate) return false
    const today = new Date()
    const [month, day, year] = selectedDate.split('/').map(Number) // Ensure correct parsing
    if (!month || !day || !year) return false
    const parsedSelectedDate = new Date(year, month - 1, day) // Month is 0-based in JS
    if (!parsedSelectedDate) return false

    return (
      parsedSelectedDate?.getFullYear() === today?.getFullYear() && parsedSelectedDate?.getMonth() === today?.getMonth()
    )
  }

  // Helper function to validate start date with end date comparison
  const validateStartDateWithEndDateRule = (rule, selectedDate, label, errorList) => {
    if (rule.type === DATE_VALIDATION_TYPE.START_DATE && rule.endDate) {
      const startDateDiff = validateStartDateAndEndDate(selectedDate, rule.endDate)
      if (startDateDiff > 0) {
        errorList.push(`${label}  ${DATE_VALIDATION_MESSAGE.CAN_NOT_BE_GREATOR_THAN_END_DATE}`)
      }
    }
  }

  // Helper function to validate start date greater than end date
  const validateStartDateGreaterRule = (rule, selectedDate, label, errorList) => {
    if (rule.type === DATE_VALIDATION_TYPE.START_DATE_GREATER && rule.endDate) {
      const isStartDateGreater = startDateShouldBeGreater(selectedDate, rule.endDate)
      if (!isStartDateGreater) {
        errorList.push(`${rule.excludeLabel ? '' : label} ${rule.msg}`)
      }
    }
  }

  // Helper function to validate start date rules (START_DATE and START_DATE_TODAY)
  const validateStartDateRules = (rule, selectedDate, label, errorList) => {
    if (rule.type === DATE_VALIDATION_TYPE.START_DATE) {
      if (validateStartDateWithCurrentDate(selectedDate)) {
        errorList.push(`${rule.excludeLabel ? '' : label}  ${rule.msg}`)
      }
    }

    if (rule.type === DATE_VALIDATION_TYPE.START_DATE_TODAY) {
      if (validateStartDateIsToday(selectedDate)) {
        errorList.push(`${rule.excludeLabel ? '' : label}  ${rule.msg}`)
      }
    }
  }

  // Helper function to validate start date required if end date is selected
  const validateStartDateRequiredIfEndDateRule = (rule, selectedDate, label, errorList) => {
    if (rule.type === DATE_VALIDATION_TYPE.START_DATE_REQUIRED_IF_END_DATE && rule.endDate) {
      if (!selectedDate && rule.endDate) {
        errorList.push(`${rule.excludeLabel ? '' : label} ${rule.msg || 'Please select a start date before selecting an end date.'}`)
      }
    }
  }

  // Helper function to validate end date rules
  const validateEndDateRules = (rule, selectedDate, label, errorList) => {
    if (rule.type === DATE_VALIDATION_TYPE.END_DATE && rule.startDate) {
      const endDateDiff = validateStartDateAndEndDate(selectedDate, rule.startDate)
      if (endDateDiff < 0) {
        errorList.push(`${rule.excludeLabel ? '' : label}  ${rule.msg}`)
      }
    }
  }

  // Helper function to validate end date greater than start date
  const validateEndDateGreaterRule = (rule, selectedDate, label, errorList) => {
    if (rule.type === DATE_VALIDATION_TYPE.END_DATE_GREATER && rule.endDate) {
      const endDateDiff = validateStartDateAndEndDate(selectedDate, rule.endDate)
      if (endDateDiff > 0) {
        errorList.push(`${rule.excludeLabel ? '' : label}  ${rule.msg}`)
      }
    }
  }

  // Helper function to validate end date required if start date is selected
  const validateEndDateRequiredIfStartDateRule = (rule, selectedDate, label, errorList) => {
    if (rule.type === DATE_VALIDATION_TYPE.END_DATE_REQUIRED_IF_START_DATE && rule.startDate) {
      if (!selectedDate && rule.startDate) {
        errorList.push(`${rule.excludeLabel ? '' : label} ${rule.msg || 'Please select an end date.'}`)
      }
    }
  }

  // Helper function to validate schedule rules
  const validateScheduleRules = (rule, selectedDate, label, errorList) => {
    if (
      rule.type === DATE_VALIDATION_TYPE.SCHEDULE_YEAR &&
      rule?.schedule?.toLowerCase() === SCHEDULE_DROPDOWN.END_OF_YEAR
    ) {
      if (!validateSameYear(selectedDate)) {
        errorList.push(`${rule.excludeLabel ? '' : label} ${rule.msg}`)
      }
    }

    if (
      rule.type === DATE_VALIDATION_TYPE.SCHEDULE &&
      rule?.schedule?.toLowerCase() === SCHEDULE_DROPDOWN.END_OF_MONTH
    ) {
      if (!validateSameMonth(selectedDate)) {
        errorList.push(`${rule.excludeLabel ? '' : label} ${rule.msg}`)
      }
    }
  }

  const validateDates = selectedDate => {
    let errorList = []
    const rules = [...validationRules]
    const label = fieldName?.replace('*', '')
    rules.forEach(rule => {
      validateStartDateWithEndDateRule(rule, selectedDate, label, errorList)
      validateStartDateGreaterRule(rule, selectedDate, label, errorList)
      validateStartDateRules(rule, selectedDate, label, errorList)
      validateStartDateRequiredIfEndDateRule(rule, selectedDate, label, errorList)
      validateEndDateRules(rule, selectedDate, label, errorList)
      validateEndDateGreaterRule(rule, selectedDate, label, errorList)
      validateEndDateRequiredIfStartDateRule(rule, selectedDate, label, errorList)
      validateScheduleRules(rule, selectedDate, label, errorList)
    })

    return errorList[0]
  }

  return {
    validateDates,
  }
}

/**
 * Calculates the end date based on the given start date and duration type.
 * @param {Date|string} startDate - The start date as a Date object or MM/DD/YYYY string.
 * @param {string} durationType - One of 'MONTHLY', 'QUARTERLY', 'ANNUAL'.
 * @returns {Date|null} The calculated end date as a Date object, or null if input is invalid.
 */
export function calculateEndDateByDuration(startDate, durationType) {
  if (!startDate) return null
  let tempDate
  if (typeof startDate === 'string') {
    // Support MM/DD/YYYY string
    const parts = startDate.split('/')
    if (parts.length !== 3) return null
    const month = parseInt(parts[0], 10)
    const day = parseInt(parts[1], 10)
    const year = parseInt(parts[2], 10)
    if (isNaN(month) || isNaN(day) || isNaN(year)) return null
    tempDate = new Date(year, month - 1, day)
  } else if (startDate instanceof Date) {
    tempDate = new Date(startDate.getTime()) // clone
  } else {
    return null
  }
  tempDate.setHours(0, 0, 0, 0)
  let newEndDateObj
  switch (durationType?.toUpperCase()) {
    case 'MONTHLY':
      tempDate.setDate(tempDate.getDate() + 30)
      newEndDateObj = tempDate
      break
    case 'QUARTERLY':
      tempDate.setDate(tempDate.getDate() + 90)
      newEndDateObj = tempDate
      break
    case 'ANNUAL':
      tempDate.setFullYear(tempDate.getFullYear() + 1)
      tempDate.setDate(tempDate.getDate() - 1)
      newEndDateObj = tempDate
      break
    default:
      newEndDateObj = tempDate
  }
  return newEndDateObj
}
